import { create } from "zustand";

/**
 * The create-task composer's unsent prompt, per organization (CONTRACT C3).
 * The modal is a dialog that closes on Escape or a click outside; the prompt
 * outlives that, a reload and an organization switch, and goes only when the
 * task is created.
 */
export type NewTaskDraft = { text: string; repoId?: string };

/**
 * Whether the unsent prompt comes back into a card opened for `scopedRepoId`.
 * The global "New task" (no repository) takes it, project and all; a project's
 * own "+" takes it only when it was written for that project.
 */
export function newTaskDraftBelongsHere(draft: NewTaskDraft, scopedRepoId: string | undefined): boolean {
  return scopedRepoId === undefined || draft.repoId === scopedRepoId;
}

type NewTaskDraftState = {
  draft: NewTaskDraft | null;
  setText: (text: string, repoId?: string) => void;
  setRepo: (repoId: string) => void;
  clear: () => void;
  /** Replaced wholesale when the organization's drafts are loaded. */
  restore: (draft: NewTaskDraft | null) => void;
};

export const useNewTaskDraft = create<NewTaskDraftState>()((set) => ({
  draft: null,
  setText: (text, repoId) => set((state) => {
    if (!text.trim()) return state.draft ? { draft: null } : state;
    if (state.draft?.text === text && (repoId === undefined || state.draft.repoId === repoId)) return state;
    return { draft: { text, repoId: repoId ?? state.draft?.repoId } };
  }),
  setRepo: (repoId) => set((state) => (state.draft && state.draft.repoId !== repoId ? { draft: { ...state.draft, repoId } } : state)),
  clear: () => set((state) => (state.draft ? { draft: null } : state)),
  restore: (draft) => set({ draft }),
}));
